import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, fromEvent } from 'rxjs';
import { BaseControlComponent, ManagerResult, srSpeak } from 'src/app/shared';
import { customvalidations } from 'src/app/shared/models/customvalidations.validator';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { SearchApi, Session } from '../..';

@Component({
    selector: 'app-manager-selector',
    template: `
    <form
      [formGroup]="formGroup"
      class="tw-w-full tw-flex tw-flex-wrap tw-items-center"
      >
      <mat-form-field
        appearance="outline"
        class=" tw-w-full tw-mr-1 tw-bg-white"
        subscriptSizing="dynamic"
        >
        <mat-label>{{ label | translation: translationRequest }}</mat-label>
        <input
          id="searchInput"
          aria-controls="ResultsListContainer"
          matInput
          #searchInput
          [placeholder]="placeHolder"
          [attr.aria-expanded]="false"
          aria-haspopup="listbox"
          aria-controls="ResultsListContainer"
          [matAutocomplete]="auto"
          [formControlName]="formControlName"
          />
          @if (formControl.invalid) {
            <mat-error>{{ errorMessage | translation: translationRequest }}</mat-error>
          }
          <mat-autocomplete
            id="ResultsListContainer"
            #auto="matAutocomplete"
            [autoActiveFirstOption]="false"
            requireSelection="true"
            hideSingleSelectionIndicator="true"
            (optionSelected)="optionSelected($event)"
            [displayWith]="getFullName"
            panelWidth="auto"
            >
            @for (option of managers; track option) {
              <mat-option
                [value]="option"
                class="tw-min-w-[256px]"
                [disabled]="option.employeeId === '__NONE'"
                >
                @if (option.employeeId === '__NONE') {
                  <span>
                    {{ option.firstName }}
                  </span>
                }
                @if (option.employeeId !== '__NONE') {
                  <span class="">
                    {{ option.firstName }} {{ option.lastName }} -
                    {{ option.employeeId }}
                  </span>
                }
              </mat-option>
            }
          </mat-autocomplete>
    
          @if (!loading && !formControl.value) {
            <button
              matSuffix
              class="cglass tw-justify-end"
              color="accent"
              [disabled]="true"
              [attr.aria-label]="'Search' | translation: translationRequest"
              >
              <mat-icon class="text-hint-icon">search</mat-icon>
            </button>
          }
    
          @if (loading) {
            <div matSuffix class="tw-pr-4">
              <div class="app-spinner-grey"></div>
            </div>
          }
    
          @if (!loading && (managerSelected || filteredManagerInSession)) {
            <button
              mat-icon-button
              matSuffix
              role="button"
              [attr.aria-label]="'Remove filtered manager' | translation: translationRequest"
              (click)="clearSearch()"
              (keydown.enter)="clearSearch()"
              >
              @if (formControl.value) {
                <mat-icon class="text-hint-icon"
                  >highlight_off</mat-icon
                  >
              }
            </button>
          }
        </mat-form-field>
      </form>
    `,
    styles: [
        `
      .close-icon {
        height: 18px;
        width: 18px;
        font-size: 18px;
        cursor: pointer;
        padding-left: 4px;
      }
    `,
    ],
    standalone: false
})
export class ManagerSelectorComponent
  extends BaseControlComponent
  implements OnInit
{
  @Input() managerLookupSupervisor: boolean = true;
  @Input() managerLookupOmni: boolean = false;
  @Input() isDelegatorSearch: boolean = false;
  @Input()
  textHint: string = '';
  @Input()
  sectionName: string;

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  
  @Output()
  supervisorSelected = new EventEmitter<ManagerResult>();

  @ViewChild('searchInput', { static: true })
  searchInput: ElementRef;

  managers: ManagerResult[] = [];

  managerSelected = false;

  loading = false;

  currentSearch: string;

  isFocused: boolean = false;

  filteredManagerInSession: any;

  ngOnInit(): void {
    this.filteredManagerInSession = sessionStorage.getItem(
      this.session.user.sessionId + 'filteredManager'
    );

    if(this.filteredManagerInSession == null){
       this.filteredManagerInSession = sessionStorage.getItem(
      this.session.user.sessionId + 'filteredManager_summary'
    );
    }
    
    this.subs.sink = fromEvent<InputEvent>(
      this.searchInput.nativeElement,
      'input'
    )
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((x) => {
        const name: string = (<any>x.target).value;
        this.search(name);
      });
  }

  constructor(private searchApi: SearchApi, private session: Session) {
    super();
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    this.FeatureLogging(
      'FilterByManager',
      '',
      this.sectionName + ' - ' + 'Filter By Manager'
    );
    this.managerSelected = true;
    this.formControl.setValue(event.option.value);
    this.supervisorSelected.emit(event.option.value);
  }

  getFullName(manager: ManagerResult) {
    if (!manager) {
      return null;
    } else {
      return `${manager.firstName} ${manager.lastName}`;
    }
  }

  clearSearch() {
    this.FeatureLogging(
      'FilterByManager',
      'Removed manager',
      this.sectionName + ' - '
    );
    this.formControl.setValue(null);
    this.managerSelected = false;
    this.managers = [];
    this.supervisorSelected.emit(null);
  }

  search(name: string) {
    this.currentSearch = name;
    this.managers = [];

    if (!name || name.trim() === '') {
      return;
    }

    if (this.managerSearchHasInvalidCharacters(name)) {
      return;
    }

    if (typeof name === 'string') {
      if (name) {
        this.loading = true;
        srSpeak('Searching...', 'polite');
        const searchPromise = this.isDelegatorSearch
          ? this.searchApi.getDelegators(name)
          : this.searchApi.getManagers(name);

        searchPromise.then((managerSearch) => {
          // discard previous in-flight searches.
          if (managerSearch.searchFilter !== this.currentSearch) {
            return;
          }

          this.loading = false;
          this.managers = managerSearch.managers;
          if (this.managers.length === 0) {
            srSpeak('No results found for ' + name, 'polite');
          } else {
            srSpeak(
              this.managers.length + ' manager/s found for ' + (name || ''),
              'polite'
            );
          }
        });
      }
    }
  }

  /** Same rules as {@link customvalidations.cannotContainSpecialCharactersName} (e.g. absence name fields). */
  private managerSearchHasInvalidCharacters(name: string): boolean {
    return (
      customvalidations.cannotContainSpecialCharactersName({
        value: name,
      } as AbstractControl) !== null
    );
  }
}
